import type { Editor } from '@tiptap/react';

import { EditorContent, useEditorState } from '@tiptap/react';
import { useEffect } from 'react';

import { BoldIcon } from '@/components/tiptap-icons/bold-icon';
import { HeadingOneIcon } from '@/components/tiptap-icons/heading-one-icon';
import { HeadingThreeIcon } from '@/components/tiptap-icons/heading-three-icon';
import { HeadingTwoIcon } from '@/components/tiptap-icons/heading-two-icon';
import { ItalicIcon } from '@/components/tiptap-icons/italic-icon';
import { ListIcon } from '@/components/tiptap-icons/list-icon';
import { ListOrderedIcon } from '@/components/tiptap-icons/list-ordered-icon';
import { StrikeIcon } from '@/components/tiptap-icons/strike-icon';
import { UnderlineIcon } from '@/components/tiptap-icons/underline-icon';
import { Button } from '@/components/tiptap-ui-primitive/button';
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '@/components/tiptap-ui-primitive/toolbar';

export const TextEditor = ({
  commentText,
  editor,
  position: _position,
}: {
  commentText: string;
  editor: Editor;
  position: number;
}) => {
  // Subscribe to editor state changes so the toolbar re-renders
  // when the cursor moves or selection changes
  const activeState = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e?.isActive('bold') ?? false,
      italic: e?.isActive('italic') ?? false,
      strike: e?.isActive('strike') ?? false,
      underline: e?.isActive('underline') ?? false,
      bulletList: e?.isActive('bulletList') ?? false,
      orderedList: e?.isActive('orderedList') ?? false,
      h1: e?.isActive('heading', { level: 1 }) ?? false,
      h2: e?.isActive('heading', { level: 2 }) ?? false,
      h3: e?.isActive('heading', { level: 3 }) ?? false,
    }),
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    const currentContent = editor.getHTML();
    const newContent = commentText || '';

    const isCurrentEmpty = !currentContent || currentContent === '<p></p>';
    const isNewEmpty = !newContent;

    if (isCurrentEmpty && isNewEmpty) {
      return;
    }
    if (currentContent === newContent) {
      return;
    }

    editor.commands.setContent(newContent, {
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });
  }, [commentText, editor]);

  return (
    <div className="text-editor">
      <Toolbar variant="fixed">
        <ToolbarGroup>
          <Button
            type="button"
            data-style="ghost"
            data-active-state={activeState.bold ? 'on' : 'off'}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon className="tiptap-button-icon" />
          </Button>
          <Button
            data-style="ghost"
            data-active-state={activeState.italic ? 'on' : 'off'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            type="button"
          >
            <ItalicIcon className="tiptap-button-icon" />
          </Button>
          <Button
            data-style="ghost"
            data-active-state={activeState.strike ? 'on' : 'off'}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            type="button"
          >
            <StrikeIcon className="tiptap-button-icon" />
          </Button>
          <Button
            data-style="ghost"
            data-active-state={activeState.underline ? 'on' : 'off'}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            type="button"
          >
            <UnderlineIcon className="tiptap-button-icon" />
          </Button>
        </ToolbarGroup>

        <ToolbarSeparator />

        <ToolbarGroup>
          <Button
            data-style="ghost"
            data-active-state={activeState.bulletList ? 'on' : 'off'}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            type="button"
          >
            <ListIcon />
          </Button>
          <Button
            data-style="ghost"
            data-active-state={activeState.orderedList ? 'on' : 'off'}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            type="button"
          >
            <ListOrderedIcon />
          </Button>
        </ToolbarGroup>
        <ToolbarSeparator />
        <ToolbarGroup>
          <Button
            data-style="ghost"
            data-active-state={activeState.h1 ? 'on' : 'off'}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 1 }).run()
            }
            type="button"
          >
            <HeadingOneIcon />
          </Button>
          <Button
            data-style="ghost"
            data-active-state={activeState.h2 ? 'on' : 'off'}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 2 }).run()
            }
            type="button"
          >
            <HeadingTwoIcon />
          </Button>
          <Button
            data-style="ghost"
            data-active-state={activeState.h3 ? 'on' : 'off'}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 3 }).run()
            }
            type="button"
          >
            <HeadingThreeIcon />
          </Button>
        </ToolbarGroup>
      </Toolbar>

      <EditorContent editor={editor} className="editor-text-field" />
    </div>
  );
};
